/**
 * ============================================
 * MP MOBILE MENU (MeshPlanet)
 * PURPOSE: Multi-level mobile drawer menu. Handles opening/closing the drawer
 *          and a stack of sliding panels (up to 3 levels) so the "Back" button
 *          always returns in the exact order the user navigated.
 * ADDED: 2026-08-26 by MeshPlanet
 *
 * Why a dedicated component instead of Horizon's `header-drawer.js`:
 * that component relies on nested <details> elements living next to the
 * hamburger <summary>. Here the drawer lives in its own section (so it can
 * expose editable blocks), while the trigger stays inside the header block.
 * A small panel stack keeps the markup flat and the animation cheap.
 * ============================================
 */

const BODY_OPEN_CLASS = 'mp-mobile-menu-open';
const ROOT_PANEL = 'root';

/**
 * Focusing an element that is still translated off-screen makes the browser
 * scroll every scrollable ancestor to reveal it, which used to leave the whole
 * drawer shifted sideways after opening a panel. Never let focus scroll.
 */
const FOCUS_OPTIONS = { preventScroll: true };

/** The theme's `desktop:hidden` breakpoint (assets/base.css). */
const DESKTOP_MIN_WIDTH = 750;
const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;

class MpMobileMenu extends HTMLElement {
  /** @type {Map<string, HTMLElement>} */
  #panels = new Map();
  /** @type {string[]} Navigation history, last entry is the visible panel */
  #stack = [ROOT_PANEL];
  /** @type {Element | null} */
  #lastFocused = null;
  /** @type {HTMLElement | null} The clipping viewport that holds every panel */
  #viewport = null;
  #desktopQuery = window.matchMedia(DESKTOP_QUERY);
  /** @type {number | null} */
  #resizeFrame = null;

  connectedCallback() {
    this.#viewport = this.querySelector('.mp-mobile-menu__panels');
    this.#panels = new Map(
      Array.from(this.querySelectorAll('[data-panel-id]')).map((panel) => [panel.dataset.panelId, panel])
    );

    this.addEventListener('click', this.#onClick);
    // Delegated on document so the trigger keeps working even if the header
    // block is re-rendered (theme editor, section rendering API).
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange);

    // Theme editor helpers: open the drawer while the section/blocks are edited.
    document.addEventListener('shopify:section:select', this.#onEditorSelect);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);
    document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
    document.addEventListener('shopify:block:deselect', this.#onEditorDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#desktopQuery.removeEventListener('change', this.#onBreakpointChange);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
    document.removeEventListener('shopify:block:deselect', this.#onEditorDeselect);
    document.body.classList.remove(BODY_OPEN_CLASS);
    window.removeEventListener('resize', this.#onResize);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get #animationSpeed() {
    return Number.parseInt(this.dataset.animationSpeed ?? '', 10) || 300;
  }

  /**
   * Handle clicks inside the drawer (panel triggers, back and close buttons).
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const panelTrigger = target.closest('[data-mp-panel-target]');
    if (panelTrigger instanceof HTMLElement && panelTrigger.dataset.mpPanelTarget) {
      event.preventDefault();
      this.push(panelTrigger.dataset.mpPanelTarget);
      return;
    }

    if (target.closest('[data-mp-back]')) {
      event.preventDefault();
      this.back();
      return;
    }

    if (target.closest('[data-mp-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  /**
   * Open/close the drawer from the header hamburger button.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-mp-mobile-menu-trigger]');
    if (!trigger) return;

    event.preventDefault();
    this.toggle();
  };

  /**
   * Escape goes one level back, or closes the drawer when on the first level.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    if (this.#stack.length > 1) {
      this.back();
    } else {
      this.close();
    }
  };

  /**
   * A drawer left open while resizing back to desktop would stay on screen on
   * top of the desktop layout, so close it when the mobile breakpoint is gone.
   * @param {MediaQueryListEvent} event
   */
  #onBreakpointChange = (event) => {
    if (!event.matches || !this.isOpen) return;

    // Always close. An earlier version only closed when the hamburger had left
    // the layout, but header.js can keep the drawer container visible on
    // desktop (its overflow check runs while the mobile menu is still hidden),
    // and that guard let the drawer survive the resize.
    this.close();
  };

  /**
   * Backup for the breakpoint listener: some browsers do not fire the
   * MediaQueryList change while a window is being dragged across the
   * breakpoint. Only attached while the drawer is open, so it costs nothing
   * the rest of the time.
   */
  #onResize = () => {
    if (this.#resizeFrame != null) return;

    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = null;
      if (this.isOpen && window.innerWidth >= DESKTOP_MIN_WIDTH) this.close();
    });
  };

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    if (!this.#isOwnEditorEvent(event)) return;
    this.open();
  };

  /** @param {Event} event */
  #onEditorDeselect = (event) => {
    if (!this.#isOwnEditorEvent(event)) return;
    this.close();
  };

  /**
   * Only react to editor events that belong to this section.
   * @param {Event} event
   */
  #isOwnEditorEvent(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const sectionId = detail?.sectionId;
    if (!sectionId) return false;

    return this.closest(`[id*="${sectionId}"]`) != null || this.id.includes(sectionId);
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open() {
    if (this.isOpen) return;

    this.#lastFocused = document.activeElement;
    this.setAttribute('open', '');
    this.#setTriggersExpanded(true);
    document.body.classList.add(BODY_OPEN_CLASS);
    window.addEventListener('resize', this.#onResize, { passive: true });

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */ (this.querySelector('[data-mp-close]'))?.focus(FOCUS_OPTIONS);
    });
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.#setTriggersExpanded(false);
    document.body.classList.remove(BODY_OPEN_CLASS);
    window.removeEventListener('resize', this.#onResize);

    if (this.#lastFocused instanceof HTMLElement) this.#lastFocused.focus(FOCUS_OPTIONS);

    // Reset to the first panel once the drawer finished sliding out, so the
    // user never sees the panels jump back while the drawer is still visible.
    setTimeout(() => {
      if (!this.isOpen) this.#reset();
    }, this.#animationSpeed);
  }

  /**
   * Show a nested panel and remember where we came from.
   * @param {string} panelId
   */
  push(panelId) {
    const panel = this.#panels.get(panelId);
    const current = this.#panels.get(this.#stack[this.#stack.length - 1] ?? ROOT_PANEL);
    if (!panel || panel === current) return;

    current?.setAttribute('data-state', 'parent');
    panel.setAttribute('data-state', 'active');
    panel.scrollTop = 0;
    this.#stack.push(panelId);
    this.#resetHorizontalScroll(panel);

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */ (panel.querySelector('[data-mp-back]'))?.focus(FOCUS_OPTIONS);
      this.#resetHorizontalScroll(panel);
    });
  }

  /** Go back one level, following the exact order the user navigated. */
  back() {
    if (this.#stack.length <= 1) return;

    const currentId = /** @type {string} */ (this.#stack.pop());
    const previousId = this.#stack[this.#stack.length - 1] ?? ROOT_PANEL;

    this.#panels.get(currentId)?.setAttribute('data-state', 'inactive');

    const previous = this.#panels.get(previousId);
    previous?.setAttribute('data-state', 'active');
    this.#resetHorizontalScroll(previous);
  }

  /**
   * Belt and braces: put the viewport and the visible panel back at x = 0.
   * Nothing in the drawer scrolls horizontally, so any offset here is a
   * browser side effect and can safely be cleared.
   * @param {HTMLElement} [panel]
   */
  #resetHorizontalScroll(panel) {
    if (this.#viewport) this.#viewport.scrollLeft = 0;
    if (panel) panel.scrollLeft = 0;
  }

  /** Collapse every panel back to the first level. */
  #reset() {
    this.#stack = [ROOT_PANEL];
    this.#resetHorizontalScroll();
    for (const [panelId, panel] of this.#panels) {
      panel.setAttribute('data-state', panelId === ROOT_PANEL ? 'active' : 'inactive');
      panel.scrollTop = 0;
      panel.scrollLeft = 0;
    }
  }

  /** @param {boolean} expanded */
  #setTriggersExpanded(expanded) {
    document
      .querySelectorAll('[data-mp-mobile-menu-trigger]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
  }
}

if (!customElements.get('mp-mobile-menu')) {
  customElements.define('mp-mobile-menu', MpMobileMenu);
}
